import { computed, toValue, type ComputedRef, type MaybeRefOrGetter, type Ref } from 'vue';
import { keepPreviousData, useQuery } from '@tanstack/vue-query';
import http from '@/services/http';
import type { IScope } from '@/types/filterBuilderTypes';
import { filterNullValues } from '@/utils/filterUtils';

/**
 * Playlists report API (custom-list switches). Backed by the nightly
 * custom_playlist_switch_stats aggregation; see the Laravel
 * PlaylistSwitchingController.
 *
 * NOT everything here is report-specific. The track and vote types below —
 * TrackVoteVoter, TrackVoteEntry, TrackVoteSummary, TrackVotesContext and
 * PlaylistTrackRow — are shared surface: around 26 files import them, including
 * TrackCard, TrackCardList, the audio player queue and its persistence, the
 * generated-playlist inspector and the live-track-usage drill-down. Nothing in
 * this file uses them any more. If the Playlists report is ever trimmed, they
 * look dead from in here and are not — check their importers before deleting.
 */

export interface PlaylistSwitchingMeta {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
  from: string;
  to: string;
}

/** One row of the most-switched-lists list. */
export interface PlaylistSwitchingListRow {
  location_zone_custom_playlist_id: number;
  name: string | null;
  code: string | null;
  status_name: string | null;
  /** The zone the list (its "source") belongs to. */
  location_zone_id: number | null;
  location_zone_name: string | null;
  source_bucket_id: number | null;
  source_bucket_name: string | null;
  shuffle: boolean | null;
  user_can_shuffle: boolean | null;
  artist_repeat_limit_hours: number | null;
  download_priority: number | null;
  status_expiration: string | null;
  created_at: string | null;
  updated_at: string | null;
  total_switch_count: number;
  distinct_switcher_count: number;
}

/** Who cast a vote. `kind` drives the row glyph colour (user=staff, contact/source=client). */
export interface TrackVoteVoter {
  kind: 'user' | 'contact' | 'source' | 'unknown';
  name: string | null;
  /** True only for the viewer's own vote — drives the filled-vs-outline glyph. */
  is_current_user: boolean;
}

/** One resolved location_zone_votes row, as the inspector shows it. */
export interface TrackVoteEntry {
  kind: 'location_zone_vote';
  id: number;
  location_zone_id: number;
  origin: 'zone' | 'master';
  /** Raw value: 1 love, 0 hate, -1 comment-only; any other int is valueless. */
  vote: number;
  vote_label: 'love' | 'hate' | 'comment' | 'other';
  has_value: boolean;
  /** Why this row is not the effective vote (null when it is eligible). */
  excluded_reason: null | 'comment_only' | 'unknown_value' | 'master_gate_off';
  comment: string | null;
  is_internal: boolean;
  vote_reason_id: number | null;
  vote_reason_name: string | null;
  vote_type_id: number | null;
  vote_type_name: string | null;
  source_name: string | null;
  voter: TrackVoteVoter;
  created_at: string | null;
  updated_at: string | null;
}

/** The full vote picture for one track in one zone context. */
export interface TrackVoteSummary {
  /**
   * The vote that decides this track in this zone: a hate beats a love
   * outright, whoever cast it and however old it is. Null when the only rows
   * are comment-only or inert. An effective hate IS the exclusion from
   * generated playlists — there is deliberately no second field saying so.
   */
  effective: TrackVoteEntry | null;
  counts: {
    love: number;
    hate: number;
    comment_only: number;
    other: number;
    comments: number;
  };
  /** Lossless, redaction-safe record, newest first. */
  entries: TrackVoteEntry[];
}

/** Zone-level vote facts, hoisted to the response envelope (null when votes aren't resolved). */
export interface TrackVotesContext {
  location_zone_id: number;
  location_zone_name: string | null;
  master_location_zone_id: number | null;
  master_location_zone_name: string | null;
  master_votes_apply: boolean;
}

/**
 * One track of a custom playlist, rich enough to render a track card (cover
 * art, title, artist, album, bitrate, ISRC, dates, copyright, duration).
 * Mirrors the Laravel PlaylistTrackResource.
 */
export interface PlaylistTrackRow {
  id: number;
  code: string | null;
  name: string | null;
  remix_name: string | null;
  artist_id: number | null;
  artist_name: string | null;
  album_id: number | null;
  album_name: string | null;
  genre_name: string | null;
  rating: string | null;
  playlist_count: number | null;
  isrc: string | null;
  bitrate: number | null;
  duration: number | null;
  copyright: number | null;
  cover_art_signed_url: string | null;
  uploaded_date: string | null;
  created_at: string | null;
  position: number | null;
  /** Nested {id, display_string} tag models, rendered as chips by CardTags. */
  tags?: Array<{ id: number; display_string: string }>;
  /**
   * Where this track's lyrics got to, named and linkable — the card renders it
   * as a chip beside the genre that opens the track's Lyrics view. Null when the
   * track has no status set; absent on surfaces whose endpoint predates it.
   */
  lyrics_check_status?: { id: number; display_string: string } | null;
  /**
   * Votes + comments for this track in the response's zone context. Present
   * (possibly null — "resolved, no visible votes") only on surfaces that
   * resolve votes AND for a viewer permitted to see them; absent otherwise.
   */
  votes?: TrackVoteSummary | null;
}

/** One row of the per-list contact breakdown. */
export interface PlaylistSwitchingContactRow {
  contact_id: number;
  display_name: string | null;
  email: string | null;
  switch_count: number;
}

/** Exact switch timestamps (ISO8601, UTC, oldest first) for one contact + list. */
export interface PlaylistSwitchingContactSwitchesResponse {
  switches: string[];
}

export interface PlaylistSwitchingListResponse {
  data: PlaylistSwitchingListRow[];
  meta: PlaylistSwitchingMeta;
}

export interface PlaylistSwitchingContactResponse {
  list: {
    location_zone_custom_playlist_id: number;
    name: string | null;
    code: string | null;
  } | null;
  /** Range-wide totals across ALL contacts (not just the returned page). */
  total_switches: number;
  total_contacts: number;
  data: PlaylistSwitchingContactRow[];
  meta: PlaylistSwitchingMeta;
}

export interface PlaylistSwitchingSummary {
  from: string;
  to: string;
  total_switches: number;
  distinct_lists_switched: number;
  distinct_switchers: number;
  history: Array<{ report_date: string; switch_count: number }>;
}

export interface PlaylistSwitchingDateRange {
  from?: string;
  to?: string;
}

export interface PlaylistSwitchingListFilters extends PlaylistSwitchingDateRange {
  q?: string;
  /** e.g. 'switch_count' (default desc) or 'list_name'; prefix '-' for desc. */
  sort?: string;
  page?: number;
  perPage?: number;
}

const ROOT_KEY = ['playlist-switching'] as const;

function appendRange(params: URLSearchParams, range: PlaylistSwitchingDateRange): void {
  if (range.from) {
    params.set('from', range.from);
  }
  if (range.to) {
    params.set('to', range.to);
  }
}

export function usePlaylistSwitchingSummary(range: MaybeRefOrGetter<PlaylistSwitchingDateRange>) {
  return useQuery({
    queryKey: computed(() => [...ROOT_KEY, 'summary', toValue(range)]),
    queryFn: () => {
      const params = new URLSearchParams();
      appendRange(params, toValue(range));
      const qs = params.toString();
      return http
        .get<PlaylistSwitchingSummary>(`/api/playlist-switching/summary${qs ? `?${qs}` : ''}`)
        .then((response) => response.data);
    },
  });
}

export interface PlaylistSwitchingSearchParams {
  pageIndex: Ref<number> | ComputedRef<number>;
  pageSize: Ref<number> | ComputedRef<number>;
  /** columnSort from usePersistentTable, e.g. { total_switch_count: 'desc' }. */
  sorting?: Ref<object> | ComputedRef<object>;
  /** compositeFilter from usePersistentTable ({ ruleScopes, ... }). */
  filter?: Ref<object> | ComputedRef<object>;
  from: Ref<string> | ComputedRef<string>;
  to: Ref<string> | ComputedRef<string>;
}

/**
 * POSTs to `/api/playlist-switching/search` so the page can drive the
 * report with the shared table tooling's IScope filter + sort spec (same shape
 * as {@link useAudioFileIssuesSearch}). Date range stays explicit (the report
 * window). Returns the paginated list response.
 */
export function usePlaylistSwitchingSearch({
  pageIndex,
  pageSize,
  sorting,
  filter,
  from,
  to,
}: PlaylistSwitchingSearchParams) {
  const queryKey = [...ROOT_KEY, 'search', pageIndex, pageSize, sorting, filter, from, to] as const;
  const queryFn = () => {
    let cleanedFilter: { ruleScopes: IScope } | undefined;
    const rawFilter = filter?.value as { ruleScopes?: IScope } | undefined;
    if (rawFilter?.ruleScopes) {
      const cleanedScope = filterNullValues(rawFilter.ruleScopes);
      if (cleanedScope) {
        cleanedFilter = { ruleScopes: cleanedScope };
      }
    }

    const size = pageSize?.value ?? 50;
    const index = pageIndex?.value ?? 0;

    return http
      .post<PlaylistSwitchingListResponse>('/api/playlist-switching/search', {
        perPage: size,
        page: Math.floor(index / size) + 1,
        sort: sorting?.value || undefined,
        filter: cleanedFilter,
        from: from.value,
        to: to.value,
      })
      .then((response) => response.data);
  };
  const query = useQuery({ queryFn, queryKey, retry: 0, placeholderData: keepPreviousData });
  return {
    ...query,
    isLoading: computed(() => query.isFetching.value || query.isLoading.value),
  };
}

export function usePlaylistSwitchingContacts(
  listId: MaybeRefOrGetter<number | null>,
  filters: MaybeRefOrGetter<PlaylistSwitchingListFilters>,
) {
  return useQuery({
    queryKey: computed(() => [...ROOT_KEY, 'contacts', toValue(listId), toValue(filters)]),
    enabled: computed(() => toValue(listId) !== null),
    placeholderData: keepPreviousData,
    queryFn: () => {
      const id = toValue(listId);
      const value = toValue(filters);
      const params = new URLSearchParams();
      appendRange(params, value);
      if (value.page) {
        params.set('page', String(value.page));
      }
      if (value.perPage) {
        params.set('per_page', String(value.perPage));
      }
      const qs = params.toString();
      return http
        .get<PlaylistSwitchingContactResponse>(`/api/playlist-switching/${id}${qs ? `?${qs}` : ''}`)
        .then((response) => response.data);
    },
  });
}

/**
 * Exact switch timestamps for one contact + list — lazy-loaded when a contact
 * card is expanded (the aggregate breakdown only has daily counts, so the
 * precise times come from the source submissions). Disabled until both ids are
 * set so it only fires for the expanded contact.
 */
export function usePlaylistSwitchingContactSwitches(
  listId: MaybeRefOrGetter<number | null>,
  contactId: MaybeRefOrGetter<number | null>,
  range: MaybeRefOrGetter<PlaylistSwitchingDateRange>,
) {
  return useQuery({
    queryKey: computed(() => [
      ...ROOT_KEY,
      'contact-switches',
      toValue(listId),
      toValue(contactId),
      toValue(range),
    ]),
    enabled: computed(() => toValue(listId) !== null && toValue(contactId) !== null),
    staleTime: 60 * 1000,
    queryFn: () => {
      const id = toValue(listId);
      const contact = toValue(contactId);
      const params = new URLSearchParams();
      appendRange(params, toValue(range));
      const qs = params.toString();
      return http
        .get<PlaylistSwitchingContactSwitchesResponse>(
          `/api/playlist-switching/${id}/contacts/${contact}/switches${qs ? `?${qs}` : ''}`,
        )
        .then((response) => response.data);
    },
  });
}
